import { PropsWithChildren, useCallback } from "react";
import {
  useWatch,
  Controller,
  FieldValues,
  ControllerFieldState,
  ControllerRenderProps,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { useQuery } from "@apollo/client";

import Select from "react-select";

import { useTranslation } from "react-i18next";

import {
  QUERY_DIGITAL_RECEPTION_MANUAL_REGISTRATIONS,
  QUERY_SHELLY_CONTROLLERS,
} from "../../../config/graphql/query";

import Input from "../../Input";

import { ReactComponent as WayfindingIcon } from "../../../assets/icons/Wayfinding.svg";
import { ReactComponent as InfoIcon } from "../../../assets/icons/Info.svg";
import { ReactComponent as OverviewIcon } from "../../../assets/icons/Overview.svg";
import { ReactComponent as EmergencyIcon } from "../../../assets/icons/Emergency.svg";
import { ReactComponent as GeneralPractitionerIcon } from "../../../assets/icons/GeneralPractitioner.svg";
import { ReactComponent as MedicationsIcon } from "../../../assets/icons/Medications.svg";

const LayoutOptionSvgIconMap: Record<number, JSX.Element> = {
  1001: <WayfindingIcon width="1.5rem" />,
  1002: <InfoIcon width="1.5rem" />,
  1003: <OverviewIcon width="1.5rem" />,
  1004: <EmergencyIcon width="1.5rem" />,
  1005: <GeneralPractitionerIcon width="1.5rem" />,
  1006: <MedicationsIcon width="1.5rem" />,
};

export const LayoutOptionFormCheckbox = (props: {
  form: {
    field:
      | ControllerRenderProps<FieldValues, `${string}.enabled`>
      | ControllerRenderProps<FieldValues, `${string}.required`>
      | ControllerRenderProps<FieldValues, `${string}.isCustom`>;
    fieldState: ControllerFieldState;
  };
  label: React.ReactNode;
  onChangePreview?: (name: string, value: any) => any;
  isDisabled?: boolean;
}) => {
  const {
    form: {
      field: { value, name, onChange },
      fieldState: { error },
    },
    label,
    onChangePreview,
    isDisabled,
  } = props;

  return (
    <div className="form-group form-check mb-1">
      <input
        id={name}
        type="checkbox"
        className="form-check-input"
        checked={value}
        onChange={(value) => {
          onChange(value);
          if (onChangePreview) {
            onChangePreview(name, value.target.checked);
          }
        }}
        disabled={isDisabled}
      />
      <label className="form-check-label user-select-none" htmlFor={name}>
        {label}
      </label>
      {!!error && <div className="invalid-feedback">{error.message}</div>}
    </div>
  );
};

export const LayoutOptionText = (props: {
  name: string;
  onChangePreview: (name: string, value: any) => any;
  language: string;
}) => {
  const { name, onChangePreview, language } = props;

  const enabled = useWatch({ name: `${name}.enabled` });

  if (!enabled) {
    return null;
  }

  return (
    <div className="form-group my-2">
      <div className="input-group ">
        <div className="input-group-prepend">
          <span className="input-group-text">{language.toUpperCase()}</span>
        </div>
        <Controller
          name={`${name}.text`}
          render={({ field: { onChange, ...props } }) => (
            <input
              onChange={(value) => {
                onChange(value);
                onChangePreview(`${name}.text`, value.target.value);
              }}
              {...props}
              className="form-control"
            />
          )}
        />
      </div>
    </div>
  );
};

export const LayoutOptionRegistration = (props: {
  name: string;
  manualRegistrations: IManualRegistration[];
  loading: boolean;
  label?: React.ReactNode;
}) => {
  const { name, manualRegistrations, loading, label } = props;

  const { t } = useTranslation(["screens"]);

  const enabled = useWatch({ name: `${name}.enabled` });

  if (!enabled) {
    return null;
  }

  return (
    <div className="form-group">
      <label>
        {label ?? t("screens:screen.settings.layout.manualRegistration")}
      </label>
      <Controller
        name={`${name}.registration`}
        render={({ field: { onChange, ...props }, fieldState: { error } }) => (
          <>
            <Select
              getOptionLabel={({ title }) => title}
              getOptionValue={({ id }) => id}
              options={manualRegistrations}
              isClearable
              isLoading={loading}
              onChange={onChange}
              {...props}
            />
            {!!error && (
              <div className="invalid-feedback">{error?.message}</div>
            )}
          </>
        )}
      />
    </div>
  );
};

const LayoutOptionShellyController = (props: {
  name: string;
  shellyControllers: IShellyController[];
  loading: boolean;
}) => {
  const { name, shellyControllers, loading } = props;

  const { t } = useTranslation(["screens"]);

  const enabled = useWatch({ name: `${name}.enabled` });

  if (!enabled) {
    return null;
  }

  return (
    <div className="form-group">
      <label>{t("screens:screen.settings.layout.bell.shellyController")}</label>
      <Controller
        name={`${name}.bellController`}
        render={({ field: { onChange, ...props }, fieldState: { error } }) => (
          <>
            <Select
              getOptionLabel={({ title }) => title}
              getOptionValue={({ id }) => id}
              options={shellyControllers}
              isClearable
              isLoading={loading}
              onChange={onChange}
              {...props}
            />
            {!!error && (
              <div className="invalid-feedback">{error?.message}</div>
            )}
          </>
        )}
      />
    </div>
  );
};

export const LayoutOptionCallingMenu = (props: {
  onChangePreview: (name: string, value: any) => any;
  communicationProvider?: "twilio" | "teams";
  language?: string;
}) => {
  const { onChangePreview, communicationProvider, language } = props;

  const { t } = useTranslation(["screens"]);

  const { control, register } = useFormContext();

  const enabled = useWatch({ name: `callReception.enabled` });

  const enabledCallMenu = useWatch({ name: `callReception.callMenu.enabled` });

  const {
    append: appendCallMenuOption,
    remove: removeCallMenuOption,
    fields: callMenuOptions,
  } = useFieldArray({
    control,
    name: "callReception.callMenu.options",
  });

  const renderCallMenuOptions = useCallback(
    (field: any, index: number) => {
      return (
        <div key={field.id}>
          <div className="d-flex mb-3">
            <div className="flex-grow-1">
              <div className="input-group mb-1">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    {language?.toUpperCase() ?? "en"}
                  </span>
                </div>
                <Input
                  {...register(`callReception.callMenu.options.${index}.label`)}
                  className="form-control"
                  defaultValue={field.label}
                />
              </div>
              {(!communicationProvider ||
                communicationProvider === "twilio") && (
                <div className="form-group">
                  <label htmlFor="emailTeams">
                    {t("screens:screen.settings.receptionist.primaryPhone")}
                  </label>
                  <Input
                    {...register(
                      `callReception.callMenu.options.${index}.phone`,
                    )}
                    className="form-control"
                    placeholder={t(
                      "screens:screen.settings.layout.phonePlaceholder",
                    )}
                    defaultValue={field.phone}
                  />
                </div>
              )}
              {communicationProvider === "teams" && (
                <div className="form-group">
                  <label htmlFor="emailTeams">
                    {t("screens:screen.settings.receptionist.emailTeams")}
                  </label>
                  <Input
                    {...register(
                      `callReception.callMenu.options.${index}.email`,
                    )}
                    autoComplete="email"
                    className="form-control"
                  />
                </div>
              )}
            </div>
            <button
              onClick={() => {
                removeCallMenuOption(index);
              }}
              className="btn btn-danger ml-3 align-self-start my-auto"
            >
              {t("common:remove")}
            </button>
          </div>
        </div>
      );
    },
    [language, removeCallMenuOption, register, t, communicationProvider],
  );

  if (!enabled) {
    return null;
  }

  return (
    <>
      <LayoutOptionText
        name="callReception"
        onChangePreview={onChangePreview}
        language={language ?? "en"}
      />
      <Controller
        name="callReception.callMenu.enabled"
        render={(form) => (
          <LayoutOptionFormCheckbox
            form={form}
            label={t("screens:screen.settings.layout.callMenu")}
          />
        )}
      />
      {enabledCallMenu && (
        <div className="pl-3 mt-2">
          {callMenuOptions.map((option, index) => {
            return renderCallMenuOptions(option, index);
          })}
          <button
            className="btn btn-secondary mb-2"
            onClick={(e) => {
              e.preventDefault();

              appendCallMenuOption({ label: "", phone: null, email: null });
            }}
          >
            {t("screens:screen.settings.layout.addOption")}
          </button>
        </div>
      )}
    </>
  );
};

export const LayoutOptionSvg = (props: {
  name: string;
  label: string;
  onChangePreview: (name: string, value: any) => any;
  availableSvgs?: number[];
}) => {
  const { name, label, onChangePreview, availableSvgs } = props;

  if (!availableSvgs) {
    return null;
  }

  const availableSvgsOptions = [
    { id: null, label: "— No Icon —" },
    ...availableSvgs.map((svg) => ({ id: svg })),
  ];

  return (
    <div className="form-group d-flex align-items-center">
      <label className="pt-2 mr-3">{label}</label>
      <Controller
        name={`${name}.svg`}
        render={({
          field: { value, onChange, ...props },
          fieldState: { error },
        }) => (
          <>
            <Select
              {...props}
              options={availableSvgsOptions}
              // @ts-ignore
              // eslint-disable-next-line react/no-unstable-nested-components
              getOptionLabel={({ id, label }) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {id === null
                    ? label || "— No Icon —"
                    : LayoutOptionSvgIconMap[id] || null}
                </div>
              )}
              getOptionValue={({ id }) => (id === null ? "null" : String(id))}
              value={
                availableSvgsOptions.find(
                  (option) =>
                    (option.id === null && value === null) ||
                    option.id === value,
                ) || null
              }
              onChange={(selected) => {
                const newValue = selected?.id ?? null;
                onChange(newValue);
                onChangePreview(`${name}.svg`, newValue);
              }}
              className="w-100"
              styles={{
                option: (provided, { isSelected }) => ({
                  ...provided,
                  backgroundColor: isSelected
                    ? "#80C8FF"
                    : provided.backgroundColor,
                }),
              }}
            />
            {!!error && (
              <div className="invalid-feedback">{error?.message}</div>
            )}
          </>
        )}
      />
    </div>
  );
};

interface LayoutOptionControllerProps extends PropsWithChildren {
  name: string;
  label: string;
  onChangePreview: (name: string, value: any) => void | undefined;
  language?: string;
  isDisabled?: boolean;
  manualRegistration?: boolean;
  shellyController?: boolean;
  screenId?: string;
}

export const LayoutOptionController: React.FC<LayoutOptionControllerProps> = (
  props,
) => {
  const {
    name,
    label,
    onChangePreview,
    language,
    isDisabled,
    manualRegistration,
    screenId,
    shellyController,
    children,
  } = props;
  const enabled = useWatch({ name: `${name}.enabled` });

  const { data: mrData, loading: mrLoading } = useQuery<{
    manualRegistrations: IManualRegistration[];
    manualRegistrationsCount: number;
  }>(QUERY_DIGITAL_RECEPTION_MANUAL_REGISTRATIONS, {
    variables: {
      filter: {
        screen: screenId,
      },
    },
    skip: !manualRegistration || !screenId,
    fetchPolicy: "network-only",
  });

  const { data: scData, loading: scLoading } = useQuery<{
    shellyControllers: IShellyController[];
  }>(QUERY_SHELLY_CONTROLLERS);

  return (
    <div style={{ marginBottom: "2.25rem" }}>
      <Controller
        name={`${name}.enabled`}
        render={(form) => (
          <LayoutOptionFormCheckbox
            form={form}
            label={label}
            onChangePreview={onChangePreview}
            isDisabled={isDisabled}
          />
        )}
      />
      {!children && (
        <>
          <LayoutOptionText
            name={name}
            onChangePreview={onChangePreview}
            language={language ?? "en"}
          />
          {manualRegistration && (
            <LayoutOptionRegistration
              name={name}
              manualRegistrations={mrData?.manualRegistrations ?? []}
              loading={mrLoading}
            />
          )}
          {shellyController && (
            <LayoutOptionShellyController
              name={name}
              shellyControllers={
                scData?.shellyControllers.filter(
                  (controller) => controller.role === "BELL",
                ) ?? []
              }
              loading={scLoading}
            />
          )}
        </>
      )}
      {enabled && children}
    </div>
  );
};
